// Copyright (c) 2024 by Audere

import {
  AddQCFeedbackRequest,
  FormLabelData,
  LabelingStatus,
  MessageRole,
  UpdateLabelRequest,
  UpdateLabelingStatusRequest,
  useAddQCFeedbackMutation,
  useUpdateLabelMutation,
  useUpdateLabelingStatusMutation,
} from "../features/api/labelApiSlice";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Circle as CircleIcon,
  CloudDoneOutlined as CloudDoneOutlinedIcon,
  CloudOutlined as CloudOutlinedIcon,
  CloudUploadOutlined as CloudUploadOutlinedIcon,
  WarningRounded as WarningRoundedIcon,
} from "@mui/icons-material";
import {
  CompletionStatus,
  LabelTaskKey,
  getFormStatus,
  getInitialQCCorrectLabels,
  getInitialTaskStatuses,
  getTaskCompletionStatus,
} from "../utils/taskUtils";
import {
  LabelTask,
  LabeledContent,
  getLabelTasks,
} from "../features/label/labelTypes";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SnackbarAlert, severity } from "./Alerts";
import { createStyleSheet, globalStyles } from "../utils/styles";
import {
  setQCCorrectLabels,
  setTaskStatuses,
  updateLabelerStatus,
  updateQCStatus,
  updateTaskStatuses,
} from "../features/slices/labelSlice";
import { useAppDispatch, useAppSelector } from "../features/store";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import AnnotatorBadge from "./AnnotatorBadge";
import ConversationLabels from "./labels/ConversationLabels";
import MessageLabels from "./labels/MessageLabels";
import SummaryLabels from "./labels/SummaryLabels";
import { hasSubmittedLabels } from "../features/data/annotatorStatusUtils";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useLabelingSchema } from "../hooks/useSchema";
import { useLabels } from "../hooks/useLabels";
import { useSummary } from "../hooks/useSummary";
import { useTranslation } from "react-i18next";
import { usesStagingApi } from "../utils/config";
import { logEvent } from "../features/api/apiSlice";
import { LabelingEvent } from "../features/api/logging";
import { useLabelsForMonitor } from "../hooks/useLabelsForMonitor";
import { getConversationHook } from "../features/data/conversationUtils";

const ConversationTasks: React.FC = () => {
  const navigate = useNavigate();
  const { isQC, isLabeler, group, email, role, isMonitor } = useCurrentUser();
  const { labelTypeID, conversationUID, taskID } = useParams();
  const dispatch = useAppDispatch();
  const useConversation = getConversationHook(role);
  const { conversation: convo, error: convoError } = useConversation(
    conversationUID ?? "",
    labelTypeID ?? ""
  );
  const taskStatuses = useAppSelector((state) => state.label.taskStatuses);
  const { summary } = useSummary(conversationUID ?? "");
  const { t } = useTranslation();
  const labeler = useAppSelector((state) => state.label.labeler);
  const qc = useAppSelector((state) => state.label.qc);
  const { labels: existingLabels } = useLabels(conversationUID ?? "");
  const { labels: existingMonitorLabels } = useLabelsForMonitor(
    conversationUID ?? ""
  );
  const [searchParams] = useSearchParams();
  const offset = searchParams.get("page");
  const [updateLabelingStatus, updateLabelingStatusResult] =
    useUpdateLabelingStatusMutation();
  const [updateLabel, updateLabelResult] = useUpdateLabelMutation();
  const [addQCFeedback, addQCFeedbackResult] = useAddQCFeedbackMutation();
  const [submitStatus, setSubmitStatus] = useState<boolean>(
    isQC
      ? hasSubmittedLabels(qc?.qc_status)
      : hasSubmittedLabels(labeler?.labeler_status)
  );
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: severity;
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const { labelingTypes, labelingTasks, labelingQuestions } =
    useLabelingSchema();
  const labelTasks: LabelTask[] = useMemo(
    () => getLabelTasks(labelTypeID, labelingTasks, labelingTypes),
    [labelTypeID, labelingTasks, labelingTypes]
  );

  const index = labelTasks.findIndex((task) => task.id === taskID);
  const activeStep = index !== -1 ? index : 0;
  const activeTask = labelTasks[activeStep];
  const logCommonData = useMemo(
    () => ({
      userEmail: email,
      userRole: role,
      userGroup: group,
      conversationUid: conversationUID ?? "",
      labelType: labelTypeID,
    }),
    [email, role, group, conversationUID, labelTypeID]
  );

  if ((isQC && !labeler) || convoError)
    navigate(`/dashboard/${labelTypeID}/conversation/${conversationUID}`);

  useEffect(() => {
    const labels = isMonitor
      ? existingMonitorLabels?.labels
      : existingLabels?.labels;
    if (isFirstLoad && labels && convo?.messages) {
      const tasks = getInitialTaskStatuses(
        labelTasks,
        labels,
        convo.messages,
        labelingQuestions
      );
      dispatch(setTaskStatuses(tasks));

      if (isQC) {
        const corrections = getInitialQCCorrectLabels(
          labelTasks,
          labels,
          convo.messages,
          labelingQuestions
        );
        dispatch(setQCCorrectLabels(corrections));
      }
      setIsFirstLoad(false);
    }
  }, [
    dispatch,
    convo?.messages,
    existingLabels?.labels,
    existingMonitorLabels?.labels,
    labelTasks,
    isFirstLoad,
    labelingQuestions,
    isQC,
    isMonitor,
  ]);

  const updateRoute = useCallback(
    (newTaskID: string) => {
      if (!labelTypeID || !conversationUID) return;
      navigate(
        `/dashboard/${labelTypeID}/conversation/${conversationUID}/${newTaskID}/edit`
      );
    },
    [labelTypeID, conversationUID, navigate]
  );

  const handleStepClick = useCallback(
    (index: number) => {
      if (index >= 0 && index < labelTasks.length) {
        const taskID = labelTasks[index].id;
        logEvent(dispatch, {
          event: LabelingEvent.StepClick,
          data: { ...logCommonData, taskID: taskID as LabelTaskKey },
        });
        updateRoute(taskID);
      }
    },
    [dispatch, labelTasks, logCommonData, updateRoute]
  );

  const hasNoAssistantMessages = () => {
    const messages = convo?.messages;
    if (!messages) return false;
    return messages.every((msg) => msg.role !== MessageRole.assistant);
  };

  const isMessageAccuracyLabelingWithoutAssistant = (taskId: string) =>
    taskId === "messageAccuracyLabeling" && hasNoAssistantMessages();

  const allTasksComplete = labelTasks.every((task) => {
    if (isMessageAccuracyLabelingWithoutAssistant(task.id)) {
      return true;
    }
    return (
      getTaskCompletionStatus(taskStatuses, task.id) ===
      CompletionStatus.complete
    );
  });

  const displayTaskStatus = () => {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        {allTasksComplete ? (
          <CheckCircleIcon color="success" />
        ) : (
          <WarningRoundedIcon color="warning" />
        )}
        <Typography
          variant="subtitle2"
          color={allTasksComplete ? "success" : "warning"}
        >
          {allTasksComplete ? t("tasks.complete") : t("tasks.incomplete")}
        </Typography>
      </Stack>
    );
  };

  const handleSubmit = useCallback(() => {
    if (!allTasksComplete) {
      setSnackbar({
        open: true,
        message: t("tasks.submitIncomplete"),
        severity: "warning",
      });
      return;
    }

    const commonPayload = {
      group: group,
      conversation_uid: conversationUID ?? "",
      labeling_type: labelTypeID ?? "",
    };

    let payload: UpdateLabelingStatusRequest;
    let successMessage: string;
    let failMessage: string;
    let event: LabelingEvent;
    if (isQC) {
      payload = {
        ...commonPayload,
        labeler_uid: labeler?.labeler_uid,
        status: submitStatus
          ? LabelingStatus.qc_in_progress
          : LabelingStatus.qc_complete,
      };
      successMessage = !submitStatus
        ? t("tasks.submitReviewSuccess")
        : t("tasks.unsubmitReviewSuccess");
      failMessage = !submitStatus
        ? t("tasks.submitReviewFail")
        : t("tasks.unsubmitReviewFail");
      event = !submitStatus
        ? LabelingEvent.SubmitReview
        : LabelingEvent.UnsubmitReview;
    } else {
      payload = {
        ...commonPayload,
        status: submitStatus
          ? LabelingStatus.label_in_progress
          : LabelingStatus.label_complete,
      };
      successMessage = !submitStatus
        ? t("tasks.submitToQCSuccess")
        : t("tasks.unsubmitFromQCSuccess");
      failMessage = !submitStatus
        ? t("tasks.submitToQCFail")
        : t("tasks.unsubmitFromQCFail");
      event = !submitStatus
        ? LabelingEvent.SubmitLabels
        : LabelingEvent.UnsubmitLabels;
    }

    updateLabelingStatus(payload)
      .unwrap()
      .then(() => {
        setSnackbar({
          open: true,
          message: successMessage,
          severity: "success",
        });
        if (isQC) {
          dispatch(updateQCStatus({ qc_status: payload.status }));
        } else if (labeler) {
          dispatch(updateLabelerStatus({ labeler_status: payload.status }));
        }
        logEvent(dispatch, {
          event,
          data: {
            ...logCommonData,
            taskID: taskID as LabelTaskKey,
            submitStatus: payload.status,
          },
        });
        setSubmitStatus((prev) => !prev);
      })
      .catch((error: unknown) => {
        console.error(t("tasks.submitToQCFail"), error);
        setSnackbar({
          open: true,
          message: failMessage,
          severity: "error",
        });
      });
  }, [
    allTasksComplete,
    conversationUID,
    updateLabelingStatus,
    submitStatus,
    t,
    group,
    labelTypeID,
    isQC,
    labeler,
    dispatch,
    logCommonData,
    taskID,
  ]);

  const saveData = (payload: UpdateLabelRequest | AddQCFeedbackRequest) => {
    const saveSuccessful = (data: FormLabelData) => {
      setLastSaved(new Date());

      const status = getFormStatus(data, activeTask, labelingQuestions, isQC);
      dispatch(
        updateTaskStatuses({
          taskID: activeTask.id,
          newTaskStatus: status,
          messageUID: payload.message_uid,
        })
      );
    };

    const saveFailed = (error: unknown) => {
      console.error("Error saving data:", error);
      setSnackbar({
        open: true,
        message: "Error saving data",
        severity: "error",
      });
    };

    if (isLabeler && "labeling_type" in payload && "label_data" in payload) {
      updateLabel(payload)
        .then(() => {
          saveSuccessful(payload.label_data);
        })
        .catch((error: unknown) => {
          saveFailed(error);
        });
    } else if (isQC && "labeler_uid" in payload && "qc_data" in payload) {
      addQCFeedback(payload)
        .then(() => {
          saveSuccessful(payload.qc_data);
        })
        .catch((error: unknown) => {
          saveFailed(error);
        });
    } else {
      throw new Error("Invalid payload for updateLabel or add QC feedback");
    }
  };

  const displayTaskStatusIcon = (taskID: string) => {
    const status = isMessageAccuracyLabelingWithoutAssistant(taskID)
      ? CompletionStatus.complete
      : getTaskCompletionStatus(taskStatuses, taskID);
    switch (status) {
      case CompletionStatus.new:
        return <CircleIcon color="disabled" />;
      case CompletionStatus.incomplete:
        return <WarningRoundedIcon color="warning" />;
      case CompletionStatus.complete:
        return <CheckCircleIcon color="success" />;
      default:
        return <CircleIcon color="disabled" />;
    }
  };

  const handleNext = () => {
    const newStep = activeStep + 1;
    if (newStep < labelTasks.length) {
      const taskID = labelTasks[newStep].id;
      logEvent(dispatch, {
        event: LabelingEvent.MoveNextStep,
        data: { ...logCommonData, taskID: taskID as LabelTaskKey },
      });
      updateRoute(taskID);
    }
  };

  const handleBack = () => {
    const newStep = activeStep - 1;
    if (newStep >= 0) {
      const taskID = labelTasks[newStep].id;
      logEvent(dispatch, {
        event: LabelingEvent.MoveBackStep,
        data: { ...logCommonData, taskID: taskID as LabelTaskKey },
      });
      updateRoute(taskID);
    }
  };

  const handleCloseTasks = () => {
    if (!labelTypeID || !conversationUID) return;
    logEvent(dispatch, {
      event: LabelingEvent.CloseTasks,
      data: { ...logCommonData, taskID: taskID as LabelTaskKey },
    });
    navigate(
      `/dashboard/${labelTypeID}/conversation/${conversationUID}?page=${offset}`
    );
  };

  const [debugEnabled, setDebugEnabled] = useState(false);
  const clickTimeoutRef = useRef<number | null>(null);
  const clickCountRef = useRef(0);
  const handleHeaderClick = () => {
    clickCountRef.current += 1;
    if (clickCountRef.current >= 3) {
      setDebugEnabled((prev) => !prev);
      clickCountRef.current = 0;
    }
    if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
    clickTimeoutRef.current = window.setTimeout(() => {
      clickCountRef.current = 0;
    }, 1000);
  };

  const displaySaveStatus = () => {
    if (updateLabelResult.isLoading || addQCFeedbackResult.isLoading) {
      return (
        <>
          <CloudUploadOutlinedIcon color="disabled" />
          <Typography variant="caption" color="textSecondary">
            Saving...
          </Typography>
        </>
      );
    } else if (lastSaved) {
      return (
        <>
          <CloudDoneOutlinedIcon color="disabled" />
          <Typography variant="caption" color="textSecondary">
            Saved
          </Typography>
        </>
      );
    } else {
      return (
        <>
          <CloudOutlinedIcon color="disabled" />
          <Typography variant="caption" color="textSecondary">
            No pending changes
          </Typography>
        </>
      );
    }
  };

  const determineLabels = () => {
    const contentType = activeTask.content;
    switch (contentType) {
      case LabeledContent.message:
      case LabeledContent.translatedMessage:
        return (
          <MessageLabels
            task={activeTask}
            debugEnabled={debugEnabled}
            onSave={saveData}
          />
        );
      case LabeledContent.summary:
        return (
          <SummaryLabels
            task={activeTask}
            debugEnabled={debugEnabled}
            summary={summary}
            onSave={saveData}
          />
        );
      default:
        return (
          <ConversationLabels
            task={activeTask}
            debugEnabled={debugEnabled}
            onSave={saveData}
          />
        );
    }
  };

  const isSubmitDisabled =
    (!allTasksComplete && !submitStatus) ||
    updateLabelResult.isLoading ||
    addQCFeedbackResult.isLoading;

  const getSubmitButtonLabel = (): string => {
    if (isQC) {
      return submitStatus ? t("tasks.unsubmitReview") : t("tasks.submitReview");
    }
    return submitStatus ? t("tasks.unsubmitFromQC") : t("tasks.submit");
  };

  return (
    <Box sx={styles.container}>
      {/* Tasks header */}
      <Box sx={styles.tasksHeader}>
        <Box sx={styles.headerTitleLabeler}>
          <Typography variant="h4" onClick={handleHeaderClick}>
            Labeling Tasks
            {usesStagingApi() && debugEnabled && (
              <Typography variant="caption" sx={{ ml: 1, color: "error.main" }}>
                [DEBUG]
              </Typography>
            )}
          </Typography>
          {labeler && isQC && (
            <Box sx={styles.selectedLabelerBox}>
              <Typography align={"center"}>Selected Labeler</Typography>
              <AnnotatorBadge annotators={[labeler]} />
            </Box>
          )}
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          {!isMonitor && (
            <>
              {displaySaveStatus()}
              {displayTaskStatus()}
              <Button
                loading={updateLabelingStatusResult.isLoading}
                variant="outlined"
                disabled={isSubmitDisabled}
                onClick={handleSubmit}
                color={submitStatus ? "warning" : "primary"}
              >
                {getSubmitButtonLabel()}
              </Button>
            </>
          )}
          <Button variant="contained" onClick={handleCloseTasks}>
            Close
          </Button>
        </Stack>
      </Box>

      {/* Stepper */}
      <Box sx={styles.stepperBox}>
        <Stepper nonLinear activeStep={activeStep}>
          {labelTasks.map((task, index) => (
            <Step
              key={task.id}
              onClick={() => {
                handleStepClick(index);
              }}
              sx={styles.stepHighlight}
            >
              <StepLabel icon={displayTaskStatusIcon(task.id)}>
                {task.name}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Task content */}
      <Box id="task-content" sx={styles.contentArea}>
        {activeTask.instructions && (
          <Typography variant="subtitle1" gutterBottom>
            {activeTask.instructions}
          </Typography>
        )}
        {determineLabels()}
        {
          <Backdrop
            sx={styles.backdrop}
            open={updateLabelingStatusResult.isLoading || isFirstLoad}
          >
            <CircularProgress color="inherit" aria-label="Loading..." />
          </Backdrop>
        }
      </Box>

      {/* Navigation buttons */}
      <Box sx={styles.navigation}>
        <Button
          onClick={handleBack}
          disabled={activeStep === 0}
          sx={{ marginRight: 1 }}
        >
          Back
        </Button>
        <Button
          onClick={handleNext}
          disabled={activeStep === labelTasks.length - 1}
          sx={{ marginLeft: 1 }}
        >
          Next
        </Button>
      </Box>

      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => {
          setSnackbar((prev) => ({ ...prev, open: false }));
        }}
      />
    </Box>
  );
};

const styles = createStyleSheet({
  container: {
    ...globalStyles.flexColumn,
    height: "100%",
    width: "100%",
  },
  tasksHeader: {
    alignItems: "center",
    borderBottom: "1px solid lightgrey",
    display: "flex",
    justifyContent: "space-between",
    padding: 2,
  },
  stepperBox: {
    flexShrink: 0,
    padding: 2,
  },
  stepHighlight: {
    cursor: "pointer",
    "& .MuiStepLabel-root .Mui-completed": {
      color: "success.main",
    },
    "& .MuiStepLabel-root .Mui-active": {
      color: "primary.main",
      fontWeight: "bold",
    },
  },
  contentArea: {
    border: "2px solid lightgrey",
    borderRadius: 1.5,
    flex: 1,
    overflowY: "auto",
    padding: 3,
  },
  summaryBox: {
    borderBottom: "1px solid lightgrey",
    padding: 2,
  },
  navigation: {
    display: "flex",
    justifyContent: "center",
    padding: 2,
  },
  selectedLabelerBox: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  headerTitleLabeler: {
    display: "flex",
    flexDirection: "row",
    justifyItems: "center",
    alignItems: "center",
    gap: 5,
  },
});

export default ConversationTasks;
